import { keydownHandler } from 'prosemirror-keymap'
import { type Command } from 'prosemirror-state'

import {
  createDeletionMark,
  hasPendingAdditionMark,
  isUnrejectedDeletion,
  markChangesKey,
  nudgeSelection,
  rangeIsSameAddition,
} from './helpers'

const TERMINATOR_RE = /[ .,;?\\/|%:"'!-#*@~]/

export const interceptBackspace: Command = (state, dispatch) => {
  if (!state.tr.selection.empty) {
    return markSelectionAsDeletion(state, dispatch)
  }

  const { tr } = state
  const { selection } = tr
  const { $anchor } = selection
  const { pos } = $anchor

  // if the cursor would collapse the block, expose default behavior
  if ($anchor.parentOffset === 0) return false

  // inside a proposed addition, expose default behavior
  if (hasPendingAdditionMark(tr.doc, pos - 1, pos)) return false

  // find the preceding character, that is not marked as proposed or approved deletion
  const blockStart = $anchor.start()
  let targetPos = pos - 1
  while (targetPos >= blockStart) {
    const node = tr.doc.nodeAt(targetPos)
    if (!node?.marks.some(isUnrejectedDeletion)) break
    targetPos--
  }

  // if every character before the cursor is already deleted, expose default behavior
  if (targetPos < blockStart) return false

  const $targetPos = tr.doc.resolve(targetPos + 1)
  const deletionMark = createDeletionMark($targetPos)
  const meta = { type: 'local-changes', id: deletionMark.attrs.id }
  // delete the target character
  tr.addMark(targetPos, targetPos + 1, deletionMark).setMeta(
    markChangesKey,
    meta,
  )

  dispatch?.(nudgeSelection(tr, targetPos - pos))
  return true
}

export const interceptDelete: Command = (state, dispatch) => {
  if (!state.tr.selection.empty) {
    return markSelectionAsDeletion(state, dispatch)
  }

  const { tr } = state
  const { selection } = tr
  const { $anchor } = selection
  const { pos } = $anchor

  // -2 for the node start and end
  const wouldCollapse = $anchor.parentOffset === $anchor.parent.nodeSize - 2

  // if the action would collapse two blocks, expose default behavior
  if (wouldCollapse) return false

  // inside a proposed addition, expose default behavior
  if (hasPendingAdditionMark(tr.doc, pos, pos + 1)) return false

  // find the following character, that is not marked as proposed or approved deletion
  const blockEnd = $anchor.end()
  let targetPos = pos
  while (targetPos < blockEnd) {
    const node = tr.doc.nodeAt(targetPos)
    if (!node?.marks.some(isUnrejectedDeletion)) break
    targetPos++
  }

  // if the cursor is truly at block end, fall through to allow the default
  // block-collapsing behaviour; if there are only already-deleted characters
  // remaining, consume the event as a no-op — returning false here would let
  // the browser physically remove the approved-deleted content
  if (targetPos >= blockEnd) return pos < blockEnd

  tr.addMark(
    targetPos,
    targetPos + 1,
    createDeletionMark(tr.doc.resolve(targetPos)),
  )
  dispatch?.(nudgeSelection(tr, targetPos - pos + 1))
  return true
}

export const markSelectionAsDeletion: Command = (state, dispatch) => {
  const { tr } = state
  const { $from, from, to } = tr.selection

  const end = tr.doc.resolve(to - 1)
  // if the entire range is within the same proposed addition, delete normally
  if (rangeIsSameAddition($from, end)) return false

  // if the proposed deletion is surrounded by empty space, or has a preceding space
  // and trailing punctuation, mark up the preceding space too
  const prevChar = tr.doc.textBetween(from - 1, from, 'x', 'x')
  const nextChar = tr.doc.textBetween(to, to + 1, 'x', 'x')
  const nudge = prevChar === ' ' && TERMINATOR_RE.test(nextChar) ? 1 : 0

  const markStart = from - nudge

  // one mark (and thus one proposal id) for the whole operation — non-contiguous
  // segments share the same id so they form a single change group in the UI
  const operationMark = createDeletionMark(tr.doc.resolve(markStart))

  // apply deletion mark only to characters not already proposed or approved deleted
  let rangeStart: number | null = null
  for (let p = markStart; p < to; p++) {
    const alreadyDeleted =
      tr.doc.nodeAt(p)?.marks.some(isUnrejectedDeletion) ?? false
    if (!alreadyDeleted) {
      if (rangeStart === null) rangeStart = p
    } else if (rangeStart !== null) {
      tr.addMark(rangeStart, p, operationMark)
      rangeStart = null
    }
  }
  if (rangeStart !== null) {
    tr.addMark(rangeStart, to, operationMark)
  }

  dispatch?.(tr.scrollIntoView())
  return true
}

export const handleKeyDown = keydownHandler({
  // fun fact: on a mac the delete key is actually backspace (unless you hold down fn)
  Backspace: interceptBackspace,
  Delete: interceptDelete,
  // the Shift modifier doesnt have a genuine purpose, Amy just noticed that it
  // provides a backdoor to 'normal' editing during a change-tracking session
  'Shift-Backspace': interceptBackspace,
})
